import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { activeStatus } from '../../../../../utils/envConfig'; // Added

const useLeadsModals = (axios, onEditSuccess, returnMutation) => {
    const [view, setView] = useState({
        details: false,
        edit: false,
    });
    const [user, setUser] = useState(null);
    const [userId, setUserId] = useState(null); // Keep this if used internally for fetchOneUser trigger
    const [formData, setFormData] = useState({});
    const [returnModalState, setReturnModalState] = useState({
        isModalOpen: false,
        selectedLead: null,
    });
    const [isBulkReturnConfirmOpen, setIsBulkReturnConfirmOpen] = useState(false);
    const [statusChecked, setStatusChecked] = useState({
        returned: true,
        recycle: false,
        dead: false,
        archived: false,
    });

    const toggle = useCallback((type) => {
        setView((prev) => ({ ...prev, [type]: !prev[type] }));
    }, []);

    const onFormCancel = useCallback(() => {
        setView({ details: false, edit: false });
        setUser(null);
        setUserId(null); // Reset userId as well
        setFormData({});
    }, []);

    const loadDetail = useCallback((id, item) => {
        setUser(item);
    }, []);

    const fetchOneUser = useCallback(async (userIdToFetch) => {
        if (!userIdToFetch) return;
        try {
            const response = await axios.get(`/users/${userIdToFetch}`); // Corrected endpoint
            if (response.data) {
                setUser(response.data);
                setFormData({ // Detailed formData transformation
                    ...response.data,
                    firstName: response.data?.first_name,
                    lastName: response.data?.last_name,
                    email: response.data?.email,
                    phone: response.data?.phone,
                    active: response.data?.active ? activeStatus[0] : activeStatus[1],
                    role: response.data?.role,
                });
            }
        } catch (error) {
            console.error("Failed to fetch user:", error);
            toast.error(error?.response?.data?.message || 'User not available!');
        }
    }, [axios]);

    const handleEditModal = useCallback(async (e, id) => {
        if (e && e.preventDefault) e.preventDefault();
        setUserId(id);
        await fetchOneUser(id);
        toggle('edit');
    }, [fetchOneUser, toggle]);

    const toggleReturnConfirm = useCallback((selectedLead = null) => {
        setReturnModalState((prev) => ({
            isModalOpen: !prev.isModalOpen,
            selectedLead: selectedLead || null,
        }));
    }, []);

    const toggleBulkReturnConfirm = useCallback(() => {
        setIsBulkReturnConfirmOpen((prev) => !prev);
    }, []);

    const handleStatusChange = useCallback((statusKey) => {
        setStatusChecked((prev) => {
            const newState = { ...prev };
            // Ensure only one checkbox can be true
            for (const key in newState) {
                newState[key] = false;
            }
            newState[statusKey] = true;
            return newState;
        });
    }, []);

    const handleEditSubmitSuccess = useCallback(() => {
        onFormCancel(); // Close modal and reset form data
        if (onEditSuccess) {
            onEditSuccess(); // Trigger refetch in parent
        }
    }, [onFormCancel, onEditSuccess]);

    const handleSingleReturnConfirm = useCallback(async () => {
        if (!returnModalState.selectedLead || !returnMutation) return;
        const selectedStatus = Object.keys(statusChecked).find(key => statusChecked[key]);
        if (!selectedStatus) {
            console.error("No status selected for return.");
            // Optionally, provide user feedback here
            return;
        }
        try {
            // The returnLeads API expects a payload object with a 'data' property containing the array of lead IDs.
            await returnMutation.mutateAsync({
                data: [returnModalState.selectedLead.id], // Pass leadId under the 'data' key
                // status: selectedStatus // The API might also expect status here, or handle it differently.
            });
            toggleReturnConfirm(); // Close modal on success
            // Potentially call onEditSuccess or a similar callback to refresh list
            if (onEditSuccess) {
                onEditSuccess();
            }
        } catch (error) {
            console.error("Failed to return lead:", error);
            // Handle error (e.g., show notification)
        }
    }, [returnModalState.selectedLead, returnMutation, statusChecked, toggleReturnConfirm, onEditSuccess]);

    const handleBulkReturnConfirm = useCallback(async (selectedItemsFromTable) => {
        if (!selectedItemsFromTable || selectedItemsFromTable.length === 0 || !returnMutation) return;
        const selectedStatus = Object.keys(statusChecked).find(key => statusChecked[key]);
        if (!selectedStatus) {
            console.error("No status selected for bulk return.");
            // Optionally, provide user feedback here
            return;
        }
        const leadIds = selectedItemsFromTable.map(item => item.id); // Assuming items have an id
        try {
            // The returnLeads API expects a payload object with a 'data' property containing the array of lead IDs.
            await returnMutation.mutateAsync({
                data: leadIds, // Pass leadIds under the 'data' key
                // status: selectedStatus // The API might also expect status here, or handle it differently.
                // Let's try without it first based on returnLeads.js JSDoc.
            });
            toggleBulkReturnConfirm(); // Close modal on success
            // Potentially call onEditSuccess or a similar callback to refresh list
            if (onEditSuccess) {
                onEditSuccess();
            }
        } catch (error) {
            console.error("Failed to bulk return leads:", error);
            // Handle error
        }
    }, [returnMutation, statusChecked, toggleBulkReturnConfirm, onEditSuccess]);

    return {
        view,
        user,
        formData,
        returnModalState,
        isBulkReturnConfirmOpen,
        statusChecked,
        // handlers
        toggle,
        onFormCancel,
        loadDetail,
        handleEditModal,
        fetchOneUser, // Exposing fetchOneUser if LeadEditModal needs to call it separately
        toggleReturnConfirm,
        toggleBulkReturnConfirm,
        handleStatusChange,
        handleEditSubmitSuccess,
        handleSingleReturnConfirm,
        handleBulkReturnConfirm,
        // setters (optional, if direct manipulation is needed outside handlers)
        // setView,
        // setUser,
        // setFormData,
        // setReturnModalState,
        // setIsBulkReturnConfirmOpen,
        setStatusChecked, // Exposing setStatusChecked
    };
};

export default useLeadsModals;